import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { RoughNotation } from "react-rough-notation";
import { MaskedLines, Reveal } from "../components/Reveal";
import { ResumeButton } from "../components/ResumeButton";
import { EditorialMarquee } from "../components/Marquee";
import { ThemedImage } from "../components/ThemedImage";
import { Star, Arrow, Sun } from "../components/Scribbles";
import { PROFILE, PHOTOS } from "../data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col justify-center px-5 md:px-10 pt-24 pb-16 overflow-hidden"
      >
        <motion.div
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-[0.06]"
          style={{
            y: textureY,
            backgroundImage:
              "url('https://images.unsplash.com/photo-1555432384-3b2fa7b650c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
          }}
        />

        <Star size={46} className="absolute top-28 right-10 md:right-24 hidden sm:block" />
        <Sun size={60} className="absolute bottom-40 left-6 md:left-16 hidden sm:block" />

        {/* Small themed polaroid pinned top-right of hero */}
        <div className="hidden md:block absolute top-32 right-10 lg:right-24 z-10 rotate-[4deg]">
          <div className="border-2 border-ink p-2 bg-sand shadow-[8px_8px_0_0_rgba(26,26,26,0.15)] w-[180px] lg:w-[220px]">
            <ThemedImage
              src={PHOTOS.portrait}
              alt="Arya"
              className="w-full h-[220px] lg:h-[260px]"
              intensity="soft"
            />
            <p className="font-handwritten text-lg text-ink text-center mt-1">hey there</p>
          </div>
        </div>

        <motion.div style={{ y: titleY }}>
          <p className="font-handwritten text-3xl md:text-4xl text-terracotta rotate-[-3deg] mb-4">
            Hello, I&apos;m
          </p>
          <h1 className="font-serif font-light leading-[0.85] tracking-tighter text-ink text-[24vw] md:text-[20vw] lg:text-[16vw] uppercase">
            <MaskedLines lines={["Arya"]} />
          </h1>
          <div className="max-w-xl md:ml-auto md:text-right mt-8">
            <MaskedLines
              lines={[PROFILE.role]}
              delay={0.5}
              className="block font-serif italic text-2xl md:text-4xl text-ink"
            />
          </div>
        </motion.div>

        <Reveal delay={1} className="mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8 max-w-5xl">
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
            {PROFILE.summary}
          </p>
          <ResumeButton testId="home-download-resume" />
        </Reveal>

        <Arrow size={70} className="absolute bottom-8 right-8 hidden md:block animate-pulse" />
      </section>

      <EditorialMarquee />

      {/* INTRO STATEMENT */}
      <section className="px-5 md:px-10 py-24 md:py-36 max-w-6xl">
        <Reveal>
          <p className="font-handwritten text-3xl text-terracotta mb-6 rotate-[-2deg]">
            a little more
          </p>
          <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-tight text-ink">
            I build{" "}
            <RoughNotation type="underline" color="#C95A3B" show strokeWidth={3} animationDelay={300}>
              Business Central
            </RoughNotation>{" "}
            systems that quietly run finance, sales and warehouse teams — the{" "}
            <RoughNotation type="circle" color="#1A1A1A" show strokeWidth={2} animationDelay={800} padding={8}>
              boring, brilliant
            </RoughNotation>{" "}
            software people actually depend on.
          </h2>
        </Reveal>

        <Reveal delay={0.2} className="mt-16 grid sm:grid-cols-3 gap-10 border-t-2 border-ink pt-10">
          <div>
            <p className="font-serif text-5xl text-terracotta">03+</p>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mt-2">Years in D365 BC</p>
          </div>
          <div>
            <p className="font-serif text-5xl text-terracotta">10+</p>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mt-2">Rollouts & upgrades</p>
          </div>
          <div>
            <p className="font-serif text-5xl text-terracotta">02</p>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mt-2">Cities · Mum & Blr</p>
          </div>
        </Reveal>
      </section>

      {/* QUICK LINKS */}
      <section className="px-5 md:px-10 pb-28">
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { to: "/handmade/projects", label: "See the work", sub: "Selected projects" },
            { to: "/handmade/about", label: "Read the story", sub: "Who is Arya" },
          ].map((item) => (
            <Reveal key={item.to}>
              <Link
                to={item.to}
                className="group flex items-center justify-between border-2 border-ink p-8 md:p-12 hover:bg-ink hover:text-sand transition-colors duration-500"
                data-testid={`home-quicklink-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div>
                  <p className="text-xs uppercase tracking-widest opacity-60">{item.sub}</p>
                  <h3 className="font-serif text-4xl md:text-5xl mt-2">{item.label}</h3>
                </div>
                <Arrow size={64} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-500 [&_path]:stroke-current" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
